// src/components/cms/tenants/registry.ts
export type TenantSlugs =
  | "bpp"
  | "canopy"
  | "doorify"
  | "onekey"
  | "stellar"
  | "twokeymls"
  | "interstllmls"
  | "cribio"
  | "mibor"
  | "grar"
  | "glar"
  | "michric"
  | "nwmls"
  | "ppar"
  | "gprmls"
  | "rmlsa"
  | "metrolist"
  | "northstar"
  | "elevatemls"
  | "mainelistings"
  | "realmls"
  | "columbus"
  | "nwor"
  | "nestfully"
  | "statewidemls"
  | "smartmls"
export type ResolvedTenantSlugs = TenantSlugs | "default"

/**
 * Set of valid tenant keys for fast lookup
 */
/**
 * Payload / env slugs that should resolve to a frontend tenant key for data-tenant + CSS.
 *
 * Slug aliasing (keep in sync with `src/lib/cms/index.ts` ENV_SLUG_TO_TENANT_SLUG):
 * | Env / Payload slug   | Frontend `data-tenant` | Notes                          |
 * |----------------------|------------------------|--------------------------------|
 * | `interstellarmls`    | `stellar`              | Payload Tenants slug           |
 * | `stellar` (CMS API)  | `interstellarmls`      | Remapped in lib/cms for fetch  |
 * | `bpp`                | `bpp`                  | CMS tenant id → cribio in lib/cms |
 * | `interstllmls`       | (typo — avoid)         | Legacy typo; use interstellarmls |
 */
const TENANT_SLUG_ALIASES: Partial<Record<string, TenantSlugs>> = {
  brokerdata: "bpp",
  interstellarmls: "stellar",
}

export const TENANT_SLUGS = new Set<TenantSlugs>([
  "bpp",
  "canopy",
  "doorify",
  "onekey",
  "stellar",
  "twokeymls",
  "interstllmls",
  "cribio",
  "mibor",
  "grar",
  "glar",
  "michric",
  "nwmls",
  "ppar",
  "gprmls",
  "rmlsa",
  "metrolist",
  "northstar",
  "elevatemls",
  "mainelistings",
  "realmls",
  "columbus",
  "nwor",
  "nestfully",
  "statewidemls",
  "smartmls",
])

/**
 * Resolves a tenant input (key or ID) to a ResolvedTenantKey.
 * Returns 'default' if input is invalid or not found.
 */
export function resolveTenantSlug(input?: string | null): ResolvedTenantSlugs {
  if (!input) return "default"

  const normalized = TENANT_SLUG_ALIASES[input] ?? input

  // Check if input is a valid tenant key
  if (TENANT_SLUGS.has(normalized as TenantSlugs)) {
    return normalized as TenantSlugs
  }

  return "default"
}

/**
 * Resolves tenant from environment variables.
 * Priority: NEXT_PUBLIC_CMS_TENANT_SLUG > CMS_TENANT_SLUG > NEXT_PUBLIC_THEME (fallback)
 */
export function resolveTenantFromEnv(
  env: Record<string, string | undefined> = process.env
): ResolvedTenantSlugs {
  const tenantSlug = env.NEXT_PUBLIC_CMS_TENANT_SLUG || env.CMS_TENANT_SLUG

  // Prefer explicit CMS tenant slug, but keep theme fallback when it is invalid.
  if (tenantSlug) {
    const resolvedFromTenantSlug = resolveTenantSlug(tenantSlug)
    if (resolvedFromTenantSlug !== "default") {
      return resolvedFromTenantSlug
    }
  }

  // Fallback to theme if tenant not explicitly set (themes and tenants often match)
  const themeName = env.NEXT_PUBLIC_THEME
  if (themeName) {
    const resolvedFromTheme = resolveTenantSlug(themeName)
    // Only use theme as tenant if it resolves to a valid tenant (not "default")
    if (resolvedFromTheme !== "default") {
      return resolvedFromTheme
    }
  }

  return "default"
}

/**
 * Tenants that publish `posts` (the blog) under a non-default base path.
 * The underlying route files stay at `src/app/posts/`; a Next.js rewrite
 * (see `next.config.mjs`) maps the override path onto those routes.
 * Doorify and GLAR both publish the blog at `/blog`. News is `src/app/news`.
 */
const TENANT_BLOG_BASE_PATH: Partial<Record<ResolvedTenantSlugs, string>> = {
  doorify: "/blog",
  glar: "/blog",
}

/**
 * Public base path for blog/post routes for the given (or current) tenant.
 * Defaults to `/posts`.
 */
export function resolveBlogBasePath(
  tenant: ResolvedTenantSlugs = resolveTenantFromEnv()
): string {
  return TENANT_BLOG_BASE_PATH[tenant] ?? "/posts"
}
